import { Node, useReactFlow } from 'reactflow';

import { FLOW_TYPE } from 'constants/flow';
import { GroupBlockData } from 'types';
import useTakeSnapshot from 'pages/Editor/sections/Flow/hooks/useTakeSnapshot';

interface UseGroupNodeExpandParams {
  id: string;
  data: GroupBlockData;
}

const useGroupNodeExpand = ({ id, data }: UseGroupNodeExpandParams) => {
  const { expand, sourceNodeIds, targetNodeIds } = data;
  const { getNodes, setNodes, setEdges } = useReactFlow();

  const { takeSnapshot } = useTakeSnapshot();

  const handleExpandClick = () => {
    const newExpand = !expand;
    const nodes = getNodes();
    const hiddenNodeIds = getAllChildNodeIds(nodes, id);

    // snapshot
    takeSnapshot({ projectChange: false });
    setNodes((nodes) => {
      return nodes.map((node) => {
        if (node.id === id) {
          const newNode = {
            ...node,
            data: {
              ...node.data,
              expand: newExpand,
            },
            zIndex: newExpand ? -1 : 0,
          };

          if (newExpand) {
            newNode.style = {
              width: newNode.data.width!,
              height: newNode.data.height!,
            };
          } else {
            newNode.data.width = newNode.style!.width;
            newNode.data.height = newNode.style!.height;

            delete newNode.style;
          }

          return newNode;
        } else if (hiddenNodeIds.includes(node.id)) {
          return {
            ...node,
            hidden: !newExpand,
          };
        }
        return node;
      });
    });

    setEdges((edges) =>
      edges.map((edge) => {
        if (hiddenNodeIds.includes(edge.source) && hiddenNodeIds.includes(edge.target)) {
          return {
            ...edge,
            hidden: !newExpand,
          };
        }

        const newEdge = { ...edge };
        if (newExpand) {
          if (edge.source === id) {
            const nodeId = (edge.sourceHandle as string).replace('_source', '');
            newEdge.source = nodeId;
          }
          if (edge.target === id) {
            const nodeId = (edge.targetHandle as string).replace('_target', '');
            newEdge.target = nodeId;
          }
        } else {
          if (sourceNodeIds.includes(edge.source)) {
            newEdge.source = id;
            newEdge.hidden = false;
          }
          if (targetNodeIds.includes(edge.target)) {
            newEdge.target = id;
            newEdge.hidden = false;
          }
        }
        return newEdge;
      })
    );
  };

  return {
    onExpandClick: handleExpandClick,
  };
};

export default useGroupNodeExpand;

function getAllChildNodeIds(nodes: Node[], id: string) {
  const groupIdStack = [id] as string[];
  const childNodeIds = [] as string[];

  while (groupIdStack.length > 0) {
    const groupId = groupIdStack.pop();

    nodes.forEach((node) => {
      if (node.parentNode === groupId) {
        if (node.type === FLOW_TYPE.NODE.GROUP_NODE && node.data.expand) {
          groupIdStack.push(node.id);
        }
        childNodeIds.push(node.id);
      }
    });
  }

  return childNodeIds;
}
